import React, { useEffect, useState, useCallback } from "react";
import * as actions from "./actions";
import {
  Box,
  Grid,
  TextField,
  Table,
  TableBody,
  Tooltip,
  TableRow,
  TableHead,
  TableCell,
  TableContainer,
  Paper,
  CircularProgress,
  Button,
  Pagination,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Typography,
  InputAdornment,
  IconButton,
  Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SEO from "../../components/SEO";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import LibraryMusicIcon from "@mui/icons-material/LibraryMusic";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StoreIcon from "@mui/icons-material/Store";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AlbumIcon from "@mui/icons-material/Album";
import PersonIcon from "@mui/icons-material/Person";
import LabelIcon from "@mui/icons-material/Label";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import {
  amazon_base_url,
  API_URL,
  apple_base_url,
  gaana_base_url,
  jiosaavn_base_url,
  kkbox_base_url,
  releaseFilters,
  spotify_base_url,
  tidal_base_url,
  wynk_base_url,
  yandex_base_url,
  youtube_base_url,
} from "../../constants";
import moment from "moment";
import CustomShowModal from "../../components/Common/CustomShowModal";
import * as actionRelease from "../Releases/CreateReleases/actions";
import _ from "lodash";
import { useHistory } from "react-router-dom";
import BookmarksIcon from "@mui/icons-material/Bookmarks";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { toastError, toastSuccess } from "../../utils";
import SelectCmp from "react-select";
import Loader from "react-loader-advanced";
import ConfirmationModal from "../../components/Common/ConfirmationModal";

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const PageContainer = styled(Box)({
  minHeight: 'calc(100vh - 64px)',
  background: 'linear-gradient(135deg, #e8eef3 0%, #dce4ec 100%)',
  padding: '32px 24px',
  position: 'relative',
});

const PageHeader = styled(Box)({
  marginBottom: '32px',
  '& h1': {
    fontSize: '2rem',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '8px',
    fontFamily: "'DM Sans', sans-serif",
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  '& p': {
    fontSize: '1rem',
    color: '#5f6368',
    fontFamily: "'DM Sans', sans-serif",
  },
});

const StatsCard = styled(Box)({
  background: 'white',
  borderRadius: '12px',
  padding: '20px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  '& .icon': {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    color: 'white',
  },
  '& .content': {
    flex: 1,
    '& h3': {
      fontSize: '1.75rem',
      fontWeight: 700,
      color: '#202124',
      marginBottom: '4px',
    },
    '& p': {
      fontSize: '0.875rem',
      color: '#5f6368',
    },
  },
});

const SearchField = styled(TextField)({
  fontFamily: "'DM Sans', sans-serif",
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: 'white',
    borderRadius: '12px',
    fontFamily: "'DM Sans', sans-serif",
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.12)',
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(26, 115, 232, 0.5)',
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1a73e8",
      borderWidth: "2px",
    },
  },
  '& .MuiOutlinedInput-input': {
    color: '#202124',
    fontWeight: 500,
  },
  '& .MuiInputLabel-root': {
    fontFamily: "'DM Sans', sans-serif",
    color: '#5f6368',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#1a73e8',
  },
});

const FilterButton = styled(Button)({
  fontFamily: "'DM Sans', sans-serif",
  background: 'white',
  color: '#202124',
  borderRadius: '12px',
  padding: '10px 20px',
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(0, 0, 0, 0.12)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'white',
    borderColor: '#1a73e8',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(26, 115, 232, 0.2)',
  },
});

const ReleaseCard = styled(Card)(({ statustype }) => {
  const getStatusColors = () => {
    switch (statustype) {
      case 'approved':
        return { bg: '#f1f8f4', border: '#4caf50' };
      case 'in_review':
        return { bg: '#fff8f0', border: '#ff9800' };
      case 'draft':
        return { bg: '#f0f7ff', border: '#2196f3' };
      case 'rejected':
        return { bg: '#fff5f5', border: '#f44336' };
      case 'takendown':
        return { bg: '#f5f5f5', border: '#616161' };
      default:
        return { bg: '#f9f9f9', border: '#9e9e9e' };
    }
  };

  const colors = getStatusColors();

  return {
    borderRadius: '12px',
    overflow: 'hidden',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    cursor: 'pointer',
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: colors.bg,
    borderLeft: `4px solid ${colors.border}`,
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: `0 12px 24px ${colors.border}40`,
      borderLeftWidth: '6px',
    },
  };
});

const AlbumArtwork = styled('img')({
  height: '200px',
  width: '100%',
  objectFit: 'cover',
  display: 'block',
});

const StatusChip = styled(Chip)(({ statustype }) => {
  const getStatusColor = () => {
    switch (statustype) {
      case 'approved':
        return { bg: '#4caf50', color: '#ffffff', border: '#2e7d32' };
      case 'in_review':
        return { bg: '#ff9800', color: '#ffffff', border: '#f57c00' };
      case 'draft':
        return { bg: '#2196f3', color: '#ffffff', border: '#1976d2' };
      case 'rejected':
        return { bg: '#f44336', color: '#ffffff', border: '#d32f2f' };
      case 'takendown':
        return { bg: '#757575', color: '#ffffff', border: '#616161' };
      default:
        return { bg: '#9e9e9e', color: '#ffffff', border: '#757575' };
    }
  };

  const colors = getStatusColor();

  return {
    backgroundColor: colors.bg,
    color: colors.color,
    border: `2px solid ${colors.border}`,
    fontWeight: 700,
    fontSize: '0.875rem',
    height: '36px',
    padding: '8px 16px',
    fontFamily: "'DM Sans', sans-serif",
    '& .MuiChip-icon': {
      fontSize: '1.125rem',
      color: 'white',
    },
  };
});

const ActionButton = styled(Button)({
  fontFamily: "'DM Sans', sans-serif",
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '8px 16px',
  fontSize: '0.875rem',
  transition: 'all 0.3s ease',
});

const DeleteButton = styled(ActionButton)({
  background: 'linear-gradient(135deg, #f44336 0%, #e91e63 100%)',
  color: 'white',
  '&:hover': {
    background: 'linear-gradient(135deg, #d32f2f 0%, #c2185b 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
  },
});

const StoreButton = styled(ActionButton)({
  background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
  color: 'white',
  '&:hover': {
    background: 'linear-gradient(135deg, #1557b0 0%, #00a589 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(26, 115, 232, 0.3)',
  },
});

const VideoButton = styled(Button)({
  fontFamily: "'DM Sans', sans-serif",
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  borderRadius: '12px',
  padding: '12px 24px',
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
  transition: 'all 0.3s ease',
  width: '100%',
  '&:hover': {
    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
  },
});

const CssTextField = styled(TextField)({
  fontFamily: "'DM Sans', sans-serif",
  color: "#fff",
  width: "100%",
  "& label": {
    fontFamily: "'DM Sans', sans-serif",
    color: "white",
  },
  "& label.Mui-focused": {
    color: "white",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "red",
  },
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset": {
      borderColor: "#10baa8",
      borderWidth: "1px",
    },
  },
});

const selectStyles = {
  control: (base, state) => ({
    ...base,
    border: `1px solid ${state.isFocused ? "#1a73e8" : "rgba(0, 0, 0, 0.12)"} `,
    boxShadow: "none",
    background: "white",
    borderRadius: "12px",
    minHeight: "56px",
    height: "56px",
    fontFamily: "'DM Sans', sans-serif",
  }),
  placeholder: (defaultStyles) => {
    return {
      ...defaultStyles,
      color: "#5f6368",
      fontFamily: "'DM Sans', sans-serif",
    };
  },
  singleValue: (defaultStyles) => {
    return {
      ...defaultStyles,
      color: "#202124",
      fontFamily: "'DM Sans', sans-serif",
    };
  },
  option: (defaultStyles, state) => {
    return {
      ...defaultStyles,
      backgroundColor: state.isSelected ? "#1a73e8" : state.isFocused ? "#e3f2fd" : "white",
      color: state.isSelected ? "white" : "#202124",
      fontFamily: "'DM Sans', sans-serif",
      cursor: "pointer",
    };
  },
  menu: (defaultStyles) => {
    return {
      ...defaultStyles,
      borderRadius: "12px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      fontFamily: "'DM Sans', sans-serif",
    };
  },
};

const StoreItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px',
  borderRadius: '12px',
  background: '#ffffff',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'rgba(26, 115, 232, 0.04)',
    borderColor: 'rgba(26, 115, 232, 0.2)',
    transform: 'translateX(4px)',
  },
});

const StoreName = styled(Typography)({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '0.95rem',
  fontWeight: 600,
  color: '#1a73e8',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
});

export default function Catalog(props) {
  const history = useHistory();

  const [catelogs, setCatelogs] = useState([]);
  const [openStore, setOpenStore] = useState(false);
  const [logos, setLogos] = useState([]);
  // const [search, setSearch] = useState('')
  // const [selectedSort, setSelectedSort] = useState(releaseFilters[0])
  const [loader, setLoader] = useState(true);
  const [selectedReleaseTitle, setSelectedReleaseTitle] = useState(null);
  const [count, setCount] = useState(0);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteReleaseId, setDeleteReleaseId] = useState(null);

  const [filters, setFilters] = useState({
    sort: releaseFilters[0],
    limit: 25,
    offset: 0,
    page: 1,
    search: "",
  });

  const delaySearch = useCallback(
    _.debounce((filters) => onGetCatelogList(filters), 500),
    []
  );

  useEffect(() => {
    onGetCatelogList(filters);
    setTimeout(() => {
      setLoader(false);
    }, 5000);
  }, []);

  const onGetCatelogList = (filters) => {
    props.setLoader(true);
    actions
      .getCatelogList({ ...filters, sort: filters.sort.value })
      .then((res) => {
        props.setLoader(false);
        setCatelogs(res.body);
        setCount(res.total);
        onFetchLogos();
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err.message);
      });
  };

  const onFetchLogos = () => {
    actionRelease.getLogos().then((res) => {
      setLogos(
        _.compact(
          res.body.map((item) => {
            if (item.type === "1") return { ...item, checked: true };
            else return null;
          })
        )
      );
    });
  };

  const handleDeleteClick = (e, id) => {
    e.stopPropagation();
    setDeleteReleaseId(id);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    props.setLoader(true);
    actionRelease
      .deleteDraft({ release_id: deleteReleaseId })
      .then((res) => {
        props.setLoader(false);
        toastSuccess(res.message);
        setConfirmDeleteOpen(false);
        setDeleteReleaseId(null);
        // Refresh the catalog list after deletion
        onGetCatelogList(filters);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err.message);
        setConfirmDeleteOpen(false);
        setDeleteReleaseId(null);
      });
  };

  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setDeleteReleaseId(null);
  };

  const onPageChange = (event, page) => {
    const offset = (page - 1) * filters.limit;
    const newFilters = { ...filters, page, offset };
    setFilters(newFilters);
    onGetCatelogList(newFilters);
  };

  const onChangeSearch = (e) => {
    const newFilters = { ...filters, search: e.target.value };
    setFilters(newFilters);
    delaySearch(newFilters);
  };

  const onSelectType = (e) => {
    const newFilters = { ...filters, offset: 0, page: 1, sort: e };
    setFilters(newFilters);
    onGetCatelogList(newFilters);
  };

  let newCatelogs = catelogs;
  // if (search) {
  //     newCatelogs = catelogs.filter(item => (
  //         (item.artist_name ? item.artist_name.toLowerCase().includes(search.toLowerCase()) : null) ||
  //         (item.release_title ? item.release_title.toLowerCase().includes(search.toLowerCase()) : null) ||
  //         (item.lable_name ? item.lable_name.toLowerCase().includes(search.toLowerCase()) : null)
  //     ))
  // }

  const LoaderStyle = {
    height: "100%",
    overflow: "hidden",
  };

  let totalPages = Math.ceil(Number(count) / filters.limit);

  const getStatusDetails = (status) => {
    switch (status) {
      case 'approved':
        return { label: 'Approved', icon: <CheckCircleIcon sx={{ fontSize: 18 }} /> };
      case 'in_review':
        return { label: 'In Review', icon: <AccessTimeIcon sx={{ fontSize: 18 }} /> };
      case 'draft':
        return { label: 'Draft', icon: <BookmarksIcon sx={{ fontSize: 18 }} /> };
      case 'rejected':
        return { label: 'Rejected', icon: <CancelIcon sx={{ fontSize: 18 }} /> };
      case 'takendown':
        return { label: 'Taken Down', icon: <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} /> };
      default:
        return { label: status, icon: null };
    }
  };

  return (
    <PageContainer>
      <SEO
        title="Catalog"
        description="Browse and manage all your distributed music releases in one place."
      />
      <Fade in timeout={800}>
        <Box>
          {/* Header Section */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, flexWrap: 'wrap', gap: 2 }}>
            <PageHeader sx={{ mb: 0 }}>
              <Typography variant="h1">
                <LibraryMusicIcon sx={{ fontSize: 40, color: '#1a73e8' }} />
                My Audio Catalog
              </Typography>
              <Typography>
                Manage and track all your audio releases in one place
              </Typography>
            </PageHeader>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, minWidth: '280px' }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                borderRadius: '16px',
                padding: '16px 28px',
                boxShadow: '0 4px 16px rgba(26, 115, 232, 0.25)',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                width: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(26, 115, 232, 0.35)',
                },
              }}>
                <Box sx={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <AlbumIcon sx={{ fontSize: 28, color: 'white' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Total Releases
                  </Typography>
                  <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: 'white', lineHeight: 1 }}>
                    {count}
                  </Typography>
                </Box>
              </Box>

              <VideoButton onClick={() => history.push("/video-catalog")}>
                Go To Video Catalog
              </VideoButton>
            </Box>
          </Box>

          {/* Search and Filter Section */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <SearchField
                  type="text"
                  variant="outlined"
                  size="medium"
                  onChange={onChangeSearch}
                  value={filters.search}
                  placeholder="Search by title, artist, label..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#5f6368' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SelectCmp
                  styles={selectStyles}
                  placeholder="Filter by status..."
                  value={filters.sort}
                  onChange={onSelectType}
                  options={releaseFilters}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Releases Grid */}
          {newCatelogs && newCatelogs.length ? (
            <>
              <Grid container spacing={3}>
                {newCatelogs.map((item, key) => {
                  let goTo = `/catalog/${item.release_id}`;
                  if (item.status === "draft") {
                    goTo = `/create_releases/${item.release_id}`;
                  }

                  const statusDetails = getStatusDetails(item.status);

                  return (
                    <Grid item xs={6} sm={4} md={3} key={key} sx={{
                      display: 'flex',
                      '@media (min-width: 3840px)': {
                        flexBasis: '16.666667%',
                        maxWidth: '16.666667%',
                      }
                    }}>
                      <ReleaseCard
                        statustype={item.status}
                        onClick={() => history.push(goTo)}
                      >
                        {/* Album Artwork */}
                        <AlbumArtwork
                          src={`${API_URL}/${item.album_artwork}`}
                          alt={item.release_title}
                          onError={(e) => {
                            // Remove error handler to prevent infinite loop
                            e.target.onerror = null;
                            // Use a data URI as fallback (no server request needed)
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2UwZTBlMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
                          }}
                        />

                        {/* Card Content */}
                        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
                          {/* Title */}
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              fontSize: '1rem',
                              mb: 1,
                              color: '#202124',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              minHeight: '48px',
                              lineHeight: 1.4,
                            }}
                          >
                            {item.release_title}
                          </Typography>

                          {/* Artist */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                            <PersonIcon sx={{ fontSize: 16, color: '#5f6368' }} />
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#202124',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {item.artist_name}
                            </Typography>
                          </Box>

                          {/* Label */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                            <LabelIcon sx={{ fontSize: 16, color: '#5f6368' }} />
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#5f6368',
                                fontSize: '0.8125rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {item.lable_name}
                            </Typography>
                          </Box>

                          {/* Tracks and Date */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <MusicNoteIcon sx={{ fontSize: 16, color: '#5f6368' }} />
                              <Typography variant="body2" sx={{ color: '#5f6368', fontSize: '0.8125rem', fontWeight: 500 }}>
                                {item.number_of_tracks} tracks
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CalendarTodayIcon sx={{ fontSize: 14, color: '#5f6368' }} />
                              <Typography variant="body2" sx={{ color: '#5f6368', fontSize: '0.8125rem', fontWeight: 500 }}>
                                {moment(Number(item.release_date)).format("DD/MM/YY")}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Status Chip - Moved to bottom */}
                          <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center' }}>
                            <StatusChip
                              statustype={item.status}
                              label={statusDetails.label}
                              icon={statusDetails.icon}
                              size="small"
                            />
                          </Box>

                          {/* Action Buttons */}
                          <Box sx={{ mt: 'auto' }} onClick={(e) => e.stopPropagation()}>
                            {(item.status === "draft" || item.status === "rejected") && (
                              <DeleteButton
                                fullWidth
                                startIcon={<DeleteOutlineIcon />}
                                onClick={(e) => handleDeleteClick(e, item.release_id)}
                              >
                                Delete
                              </DeleteButton>
                            )}
                            {item.status === "approved" && (
                              <StoreButton
                                fullWidth
                                startIcon={<StoreIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedReleaseTitle(item.release_title);
                                  setOpenStore(true);
                                }}
                              >
                                View Stores
                              </StoreButton>
                            )}
                          </Box>
                        </CardContent>
                      </ReleaseCard>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Pagination */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={filters.page || 0}
                  onChange={onPageChange}
                  showFirstButton
                  showLastButton
                  size="large"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 600,
                      backgroundColor: 'white',
                      border: '2px solid #e0e0e0',
                      color: '#202124',
                      fontSize: '1rem',
                      minWidth: '40px',
                      height: '40px',
                      margin: '0 4px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        borderColor: '#1a73e8',
                        transform: 'translateY(-2px)',
                      },
                    },
                    '& .Mui-selected': {
                      background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%) !important',
                      color: 'white !important',
                      border: 'none !important',
                      transform: 'scale(1.1)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1557b0 0%, #00a589 100%) !important',
                      },
                    },
                    '& .MuiPaginationItem-ellipsis': {
                      color: '#5f6368',
                      fontWeight: 700,
                    },
                  }}
                />
              </Box>
            </>
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                background: 'white',
                borderRadius: '16px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              }}
            >
              <LibraryMusicIcon sx={{ fontSize: 80, color: '#e0e0e0', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#5f6368', fontWeight: 600 }}>
                No Releases Found
              </Typography>
              <Typography variant="body2" sx={{ color: '#9e9e9e', mt: 1 }}>
                Create your first release to get started
              </Typography>
            </Box>
          )}
        </Box>
      </Fade>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={confirmDeleteOpen}
        title="Delete Release"
        message="Are you sure you want to delete this release? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Stores Modal */}
      <CustomShowModal
        open={openStore}
        handleClose={() => setOpenStore(false)}
        maxWidth="md"
      >
        <Loader
          className="platform-loader"
          style={loader ? LoaderStyle : null}
          message={<CircularProgress size={50} style={{ color: "black" }} />}
          show={loader}
        >
          <Grid container spacing={2}>
            {logos && logos.length
              ? logos.map((item, key) => {
                  if (item.name === "Spotify") {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <StoreItem>
                          <img src={item.logo} alt="" height="32" style={{ objectFit: 'contain' }} />
                          <a
                            href={spotify_base_url.concat(selectedReleaseTitle)}
                            target="_blank"
                            rel="noreferrer"
                            style={{ textDecoration: 'none', flex: 1 }}
                          >
                            <StoreName>{item.name}</StoreName>
                          </a>
                        </StoreItem>
                      </Grid>
                    );
                  }
                  if (item.name === "Apple Music") {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <StoreItem>
                          <img src={item.logo} alt="" height="32" style={{ objectFit: 'contain' }} />
                          <a
                            href={apple_base_url.concat(selectedReleaseTitle)}
                            target="_blank"
                            rel="noreferrer"
                            style={{ textDecoration: 'none', flex: 1 }}
                          >
                            <StoreName>{item.name}</StoreName>
                          </a>
                        </StoreItem>
                      </Grid>
                    );
                  }
                  if (item.name === "Amazon Music") {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <StoreItem>
                          <img src={item.logo} alt="" height="32" style={{ objectFit: 'contain' }} />
                          <a
                            href={amazon_base_url.concat(selectedReleaseTitle)}
                            target="_blank"
                            rel="noreferrer"
                            style={{ textDecoration: 'none', flex: 1 }}
                          >
                            <StoreName>{item.name}</StoreName>
                          </a>
                        </StoreItem>
                      </Grid>
                    );
                  }
                  if (item.name === "JioSaavn") {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <StoreItem>
                          <img src={item.logo} alt="" height="32" style={{ objectFit: 'contain' }} />
                          <a
                            href={jiosaavn_base_url.concat(
                              selectedReleaseTitle
                            )}
                            target="_blank"
                            rel="noreferrer"
                            style={{ textDecoration: 'none', flex: 1 }}
                          >
                            <StoreName>{item.name}</StoreName>
                          </a>
                        </StoreItem>
                      </Grid>
                    );
                  }
                  if (item.name === "Wynk Music") {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <StoreItem>
                          <img src={item.logo} alt="" height="32" style={{ objectFit: 'contain' }} />
                          <a
                            href={wynk_base_url.concat(selectedReleaseTitle)}
                            target="_blank"
                            rel="noreferrer"
                            style={{ textDecoration: 'none', flex: 1 }}
                          >
                            <StoreName>{item.name}</StoreName>
                          </a>
                        </StoreItem>
                      </Grid>
                    );
                  }
                  if (item.name === "Gaana") {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <StoreItem>
                          <img src={item.logo} alt="" height="32" style={{ objectFit: 'contain' }} />
                          <a
                            href={gaana_base_url.concat(selectedReleaseTitle)}
                            target="_blank"
                            rel="noreferrer"
                            style={{ textDecoration: 'none', flex: 1 }}
                          >
                            <StoreName>{item.name}</StoreName>
                          </a>
                        </StoreItem>
                      </Grid>
                    );
                  }
                  if (item.name === "Tidal") {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <StoreItem>
                          <img src={item.logo} alt="" height="32" style={{ objectFit: 'contain' }} />
                          <a
                            href={tidal_base_url.concat(selectedReleaseTitle)}
                            target="_blank"
                            rel="noreferrer"
                            style={{ textDecoration: 'none', flex: 1 }}
                          >
                            <StoreName>{item.name}</StoreName>
                          </a>
                        </StoreItem>
                      </Grid>
                    );
                  }
                  if (item.name === "Youtube Music") {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <StoreItem>
                          <img src={item.logo} alt="" height="32" style={{ objectFit: 'contain' }} />
                          <a
                            href={youtube_base_url.concat(selectedReleaseTitle)}
                            target="_blank"
                            rel="noreferrer"
                            style={{ textDecoration: 'none', flex: 1 }}
                          >
                            <StoreName>{item.name}</StoreName>
                          </a>
                        </StoreItem>
                      </Grid>
                    );
                  }
                  if (item.name === "KK Box") {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <StoreItem>
                          <img src={item.logo} alt="" height="32" style={{ objectFit: 'contain' }} />
                          <a
                            href={kkbox_base_url.concat(selectedReleaseTitle)}
                            target="_blank"
                            rel="noreferrer"
                            style={{ textDecoration: 'none', flex: 1 }}
                          >
                            <StoreName>{item.name}</StoreName>
                          </a>
                        </StoreItem>
                      </Grid>
                    );
                  }
                  if (item.name === "Yandex") {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <StoreItem>
                          <img src={item.logo} alt="" height="32" style={{ objectFit: 'contain' }} />
                          <a
                            href={yandex_base_url.concat(selectedReleaseTitle)}
                            target="_blank"
                            rel="noreferrer"
                            style={{ textDecoration: 'none', flex: 1 }}
                          >
                            <StoreName>{item.name}</StoreName>
                          </a>
                        </StoreItem>
                      </Grid>
                    );
                  }
                  return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      <StoreItem>
                        <img src={item.logo} alt="" height="32" style={{ objectFit: 'contain' }} />
                        <StoreName component="span" sx={{ color: '#202124' }}>
                          {item.name}
                        </StoreName>
                      </StoreItem>
                    </Grid>
                  );
                })
              : null}
          </Grid>
        </Loader>
      </CustomShowModal>
    </PageContainer>
  );
}
